import { Link } from "react-router-dom";
import { ArrowRight, Compass, Shield, Lightning, Globe } from "@phosphor-icons/react";
import SoftwareWeUse from "@/components/SoftwareWeUse";

const PILLARS = [
  { icon: Compass, title: "Land surveying only", desc: "No structural, no architectural. Narrow focus = deep expertise across all 13 survey types." },
  { icon: Shield, title: "Surveyor-trained drafters", desc: "Our drafters are trained by licensed surveyors. Closure, layering and annotation are internal non-negotiables." },
  { icon: Lightning, title: "24–72 hour turnaround", desc: "Plot plans in 24 hrs. Full ALTA/NSPS jobs in 3–5 business days. Rush options available." },
  { icon: Globe, title: "All 50 states covered", desc: "We know state-specific standards — Texas GLO calls, New England rod-and-link, Louisiana civil law." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative bg-zinc-50 border-b border-zinc-200 overflow-hidden ruler-top">
        <div className="absolute inset-0 bg-blueprint-grid pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-14 md:pb-20">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
            About · cad4surveys
          </div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium text-zinc-950 max-w-4xl leading-[0.95]">
            A CAD drafting studio, built for surveyors.
          </h1>
          <p className="mt-6 text-lg text-zinc-600 max-w-2xl leading-relaxed">
            We exist to take the drafting off your surveyor's plate — without compromising on
            quality, jurisdiction accuracy, or the stamp they put on it.
          </p>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
              Our story
            </div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-zinc-950 leading-tight">
              Built from a surveyor's frustration.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-lg text-zinc-600 leading-relaxed">
            <p>
              cad4surveys was founded to solve a single problem: licensed surveyors spending
              too much time on drafting instead of field work, client relationships, and
              growing their practice.
            </p>
            <p>
              Our drafters are cross-trained to understand the <em>why</em> behind every
              bearing, setback and easement — not just the <em>how</em> of drawing lines in
              AutoCAD. We treat your template as sacred. We deliver files that drop straight
              into your QA flow.
            </p>
            <p>
              Today we support firms from solo PLSs in the Rocky Mountain West to
              multi-office civil engineering firms on the East Coast — all 50 states, every
              jurisdiction.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-zinc-50 py-24 md:py-32 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 max-w-3xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
              Why choose us
            </div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-zinc-950 leading-[1.05]">
              Four reasons firms keep coming back.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
            {PILLARS.map((p, i) => (
              <div key={p.title} className="bg-white p-8 md:p-10">
                <div className="flex items-center justify-between">
                  <p.icon size={32} weight="duotone" className="text-[#0044FF]" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-8 font-display text-2xl text-zinc-950 tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-3 text-zinc-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SoftwareWeUse />

      <section className="bg-[#0044FF] text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-10 items-center">
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05]">
            Ready to see how we draft?
          </h2>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link
              to="/free-trial"
              className="px-6 py-4 bg-white text-[#0044FF] font-medium btn-lift"
              data-testid="about-cta-free-trial"
            >
              Start Free Trial
            </Link>
            <Link
              to="/contact"
              className="px-6 py-4 border border-white text-white font-medium btn-lift hover:bg-white/10 inline-flex items-center gap-2"
              data-testid="about-cta-contact"
            >
              Talk to us <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
